import React, { useState } from "react";
import toast from "react-hot-toast";
import { useGetProductGroupsQuery, useBulkSetProductWarrantyMutation } from "../productsApi";

/**
 * Flags a whole product group as carrying a warranty, with a default period.
 *
 * HasWarranty is the only equipment classification in this system - an unflagged product
 * can never enter a customer's equipment register - so a 6,700-product catalogue had to be
 * classifiable without 6,700 individual edits. Groups are the practical unit: roughly 250
 * decisions instead.
 *
 * The server refuses a request with neither a group nor an explicit product list, so this
 * dialog cannot be used to reflag an entire branch by accident.
 */
const BulkWarrantyModal = ({ open, onClose, branchId }) => {
  const [groupId, setGroupId] = useState("");
  const [hasWarranty, setHasWarranty] = useState(true);
  const [months, setMonths] = useState(12);

  const { data: groups = [] } = useGetProductGroupsQuery(undefined, { skip: !open });
  const [bulkSet, { isLoading }] = useBulkSetProductWarrantyMutation();

  const branchGroups = branchId ? groups.filter((g) => g.branchId === Number(branchId)) : groups;

  const handleApply = async () => {
    if (!groupId) {
      toast.error("Choose a product group first.");
      return;
    }
    if (hasWarranty && !(Number(months) > 0)) {
      toast.error("Default warranty period must be more than zero months.");
      return;
    }

    try {
      const result = await bulkSet({
        branchId: Number(branchId),
        productGroupId: Number(groupId),
        productCodes: null,
        hasWarranty,
        defaultWarrantyMonths: hasWarranty ? Number(months) : null,
      }).unwrap();

      toast.success(`${result.affectedCount} product(s) updated.`);
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || "Could not apply that setting.");
    }
  };

  if (!open) return null;

  const selectedGroup = branchGroups.find((g) => String(g.groupId) === String(groupId));

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h4 className="mb-0">Set Warranty by Group</h4>
              <small className="text-muted">Marks which products can be tracked as customer equipment</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {!branchId && (
              <div className="alert alert-warning">Choose a branch first — product groups are per branch.</div>
            )}

            <div className="mb-3">
              <label className="form-label">Product group</label>
              <select
                className="form-select"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={!branchId}
              >
                <option value="">Select a group…</option>
                {branchGroups.map((g) => (
                  <option key={`${g.branchId}-${g.groupId}`} value={g.groupId}>
                    {g.groupName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-check mb-3">
              <input
                id="bulkHasWarranty"
                type="checkbox"
                className="form-check-input"
                checked={hasWarranty}
                onChange={(e) => setHasWarranty(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="bulkHasWarranty">
                Products in this group carry a warranty
              </label>
            </div>

            {hasWarranty && (
              <div className="mb-3">
                <label className="form-label">Default warranty period (months)</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                />
                <small className="text-muted">
                  Suggested automatically whenever one of these products is added to a customer's equipment.
                  It can still be changed per item.
                </small>
              </div>
            )}

            {!hasWarranty && (
              <div className="alert alert-warning mb-0">
                Clearing the flag also clears each product's default period, and these products will no longer
                be selectable as customer equipment. Equipment already recorded is not affected.
              </div>
            )}

            {selectedGroup && (
              <p className="text-muted mb-0 mt-3">
                This applies to every product in <strong>{selectedGroup.groupName}</strong>.
              </p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApply}
              disabled={isLoading || !groupId || !branchId}
            >
              {isLoading ? "Applying…" : "Apply to group"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkWarrantyModal;
